import { PLAN_FEATURE_MIN_PLAN, type FieldType, type IconName, type PlanKey, type TypeCategory } from "@sagot/constants";
import type { ChipTone } from "@sagot/ui";
import type { CustomerMode, FieldObject, ServiceModuleKey } from "./settings-types";

/** The switchable modules in the order Service settings lists them (scope TKT-203). */
export const MODULE_ORDER: readonly ServiceModuleKey[] = ["tickets", "organisations", "assets", "projects", "visits", "orders"];

export const MODULE_ICON: Readonly<Record<ServiceModuleKey, IconName>> = {
  tickets: "ticket",
  organisations: "building-2",
  assets: "cpu",
  projects: "hard-hat",
  visits: "truck",
  orders: "shopping-cart",
};

/** The lowest plan that includes a module (tickets and organisations come with the helpdesk). */
export const MODULE_MIN_PLAN: Readonly<Record<ServiceModuleKey, PlanKey>> = {
  tickets: PLAN_FEATURE_MIN_PLAN.helpdesk,
  organisations: PLAN_FEATURE_MIN_PLAN.helpdesk,
  assets: PLAN_FEATURE_MIN_PLAN.after_sales,
  projects: PLAN_FEATURE_MIN_PLAN.after_sales,
  visits: PLAN_FEATURE_MIN_PLAN.after_sales,
  orders: PLAN_FEATURE_MIN_PLAN.commerce,
};

export const CUSTOMER_MODES: readonly CustomerMode[] = ["b2b", "b2c", "mixed"];

/** The sections a pack preview lists, in reading order. */
export const PACK_PART_ORDER = [
  "modules",
  "customer_mode",
  "vocabulary",
  "ticket_types",
  "type_labels",
  "fields",
  "type_fields",
  "status_labels",
  "resolution_codes",
  "macros",
  "csat_reasons",
  "calendars",
  "sla_policies",
] as const;

/** The ticket number prefix: 2 to 5 capital letters, set once (TKT-010); mirrors backend `TICKET_NUMBER_PREFIX_PATTERN`. */
export const TICKET_PREFIX_PATTERN = /^[A-Z]{2,5}$/;
/** Ticket numbers count up from here, so the example shown under the prefix field is the first one (TKT-009). */
export const TICKET_NUMBER_EXAMPLE = 10_001;

/** Words a pack or the workspace may rename (TKT-201); the labels in use are `settings.vocabulary[key]`. */
export const VOCABULARY_TERMS = ["organisation", "contact", "asset", "project", "project_plural", "stage", "stage_plural", "open_item", "visit"] as const;
export const VOCABULARY_LABEL_MAX = 60;
export const VOCABULARY_KEYS_MAX = 40;
export const VOCABULARY_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

/** Design-token names a status colour may take (mirror of backend `STATUS_COLOUR_TOKENS`); the API never stores a colour value. */
export const STATUS_COLOUR_TOKENS = ["neutral", "info", "primary", "accent", "success", "warning", "danger"] as const;
export type StatusColourToken = (typeof STATUS_COLOUR_TOKENS)[number];
export const STATUS_COLOUR_TONE: Readonly<Record<StatusColourToken, ChipTone>> = {
  neutral: "muted",
  info: "info",
  primary: "brand",
  accent: "accent",
  success: "success",
  warning: "warning",
  danger: "danger",
};
export const STATUS_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const STATUS_LABEL_MAX = 60;

/** The lucide icons a ticket type may carry (mirror of backend `TICKET_TYPE_ICONS`); anything else shows the default. */
export const TYPE_ICON_NAMES = [
  "triangle-alert", "clipboard-list", "shield-check", "hammer", "calendar-clock", "circle-help", "package", "file-text",
  "message-square", "receipt", "list-checks", "layers", "wrench", "life-buoy", "bug", "building-2", "credit-card", "phone",
  "mail", "map-pin", "monitor", "printer", "truck", "zap", "thermometer", "heart", "star", "settings", "graduation-cap",
  "stethoscope", "shopping-cart", "users",
] as const satisfies readonly IconName[];
export const DEFAULT_TYPE_ICON: IconName = "tag";

export function typeIcon(name: string | null): IconName {
  return TYPE_ICON_NAMES.find((candidate) => candidate === name) ?? DEFAULT_TYPE_ICON;
}

export const TYPE_CATEGORY_CHIP: Readonly<Record<TypeCategory, { tone: ChipTone; icon: IconName }>> = {
  customer: { tone: "brand", icon: "users" },
  back_office: { tone: "muted", icon: "lock" },
  tracker: { tone: "accent", icon: "layers" },
};

export const FIELD_TYPE_ICON: Readonly<Record<FieldType, IconName>> = {
  text: "text-cursor-input",
  long_text: "file-text",
  number: "hash",
  integer: "hash",
  bool: "check-circle",
  date: "calendar",
  datetime: "calendar-clock",
  dropdown: "chevron-down",
  multi_select: "list-checks",
  asset: "cpu",
  site: "map-pin",
  order: "shopping-cart",
  contact: "contact",
  files: "paperclip",
  serial: "scan-text",
};

/** The objects a workspace defines fields on, in the order the Fields tab lists them. */
export const FIELD_OBJECTS: readonly FieldObject[] = ["ticket", "organisation", "contact"];
/** Organisation and contact fields hold plain values; pickers over records and attachments exist on tickets only. */
export const PLAIN_FIELD_TYPES: readonly FieldType[] = ["text", "long_text", "number", "integer", "bool", "date", "datetime", "dropdown", "multi_select", "serial"];

/** Which bounds each field type takes (mirror of backend `BOUNDS_BY_TYPE`). */
export const FIELD_BOUNDS = {
  number: ["min", "max"],
  integer: ["min", "max"],
  text: ["max_length"],
  long_text: ["max_length"],
  serial: ["max_length", "format_hint"],
  multi_select: ["max_selected"],
  files: ["max_files"],
} as const satisfies Partial<Record<FieldType, readonly string[]>>;

export const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
/** An option value as the API stores it (`OPTION_VALUE_PATTERN`). */
export const OPTION_VALUE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,39}$/;
export const OPTION_VALUE_MAX = 40;
export const CONDITION_VALUES_MAX = 20;

/** The plan allowance behind each tab's "x of y" line and its upgrade hint (`SERVICE_DESK_PLAN_LIMITS`). */
export const TAB_PLAN_LIMIT = { types: "customTicketTypes", fields: "customTicketFields", statuses: "customTicketStatuses" } as const;

export const RESOLUTION_CODE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const RESOLUTION_CODE_LABEL_MAX = 80;

/** The type editor's address: `/w/:ws/service/settings/types/:typeId` (the Service settings route takes one segment, so this one is its own). */
export const TYPE_EDITOR_PATH = "/w/:ws/service/settings/types/:typeId";

/** Portal groups are stored as their English name; the key picks the translated label. */
export const PORTAL_GROUP_KEY: Readonly<Record<string, string>> = {
  Problems: "problems",
  Requests: "requests",
  "Orders & billing": "orders",
  Feedback: "feedback",
};

export function portalGroupKey(group: string | null): string | null {
  if (group === null) return null;
  return PORTAL_GROUP_KEY[group] ?? null;
}
